import { useEffect, useState } from 'react';

export type PortalHtmlBlobLoadState = 'loading' | 'ready' | 'error';

/**
 * Fetch hosted portal HTML (3D model) and expose a blob: URL for same-origin iframe embed.
 * Mirrors HtmlViewerModal fetch logic.
 */
export function usePortalHtmlBlobSrc(url: string | undefined, fetchUrl?: string, enabled = true) {
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<PortalHtmlBlobLoadState>('loading');
  const [usedDirectFallback, setUsedDirectFallback] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    const sourceUrl = (fetchUrl || url || '').trim();

    setLoadState(enabled ? 'loading' : 'ready');
    setIframeSrc(null);
    setUsedDirectFallback(false);

    if (!enabled) {
      return () => undefined;
    }

    if (!sourceUrl) {
      setLoadState('error');
      return () => undefined;
    }

    void (async () => {
      try {
        const resp = await fetch(sourceUrl);
        if (!resp.ok) throw new Error(String(resp.status));
        const blob = await resp.blob();
        if (blob.size === 0) throw new Error('empty');
        const htmlBlob =
          blob.type && blob.type !== 'application/octet-stream'
            ? blob
            : new Blob([blob], { type: 'text/html' });
        objectUrl = URL.createObjectURL(htmlBlob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setIframeSrc(objectUrl);
        setLoadState('ready');
      } catch {
        if (cancelled) return;
        const direct = (url || fetchUrl || '').trim();
        if (direct) {
          setIframeSrc(direct);
          setUsedDirectFallback(true);
          setLoadState('ready');
        } else {
          setLoadState('error');
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, fetchUrl, enabled]);

  return { iframeSrc, loadState, usedDirectFallback };
}
